/**
 * EmailHtmlFrame — renders sanitized email HTML inside an open Shadow DOM so
 * inbound <style> blocks (@media, prefers-color-scheme, :hover, ::before) apply
 * inside the message without leaking into the TSH app chrome.
 *
 * Shadow DOM (not a sandboxed iframe) avoids Chrome "Blocked script execution
 * … allow-scripts" console noise from DevTools/extensions while still isolating
 * styles. Scripts inserted via innerHTML do not execute; event-handler attrs are
 * stripped server-side by EmailHtmlSanitizer.
 *
 * Base CSS + body prep + link policy come from @tshmanager/email-shared.
 * Mobile uses `buildEmailBodyDocument` in a WebView with the same canvas defaults.
 *
 * Props:
 *   html {string} — already server-sanitized body_html (EmailHtmlSanitizer)
 *   linkClicks {Array<{url, count}>} — optional SES per-URL click counts
 */
import { h } from 'preact'
import { useRef, useEffect } from 'preact/hooks'
import {
  prepareEmailBodyHtml,
  emailBodyBaseCss,
  adaptEmailHtmlForIsolatedRoot,
  annotateEmailBodyLinkClicks,
  EMAIL_BODY_CANVAS_DEFAULTS,
  classifyEmailBodyUrl,
  shouldOpenExternally,
} from '@tshmanager/email-shared'
import { createPhotoSwipeLightbox } from '../../lib/photoswipe-lightbox.js'

const ROOT_CLASS = 'email-html-root'

function hardenLinks(root) {
  root.querySelectorAll('a[href]').forEach((a) => {
    const href = (a.getAttribute('href') || '').trim()
    const kind = classifyEmailBodyUrl(href)

    if (kind === 'empty' || kind === 'fragment' || kind === 'mailto' || kind === 'tel') {
      return
    }

    if (kind === 'unsafe' || !shouldOpenExternally(kind)) {
      a.removeAttribute('href')
      a.setAttribute('role', 'link')
      a.setAttribute('aria-disabled', 'true')
      return
    }

    a.setAttribute('target', '_blank')
    a.setAttribute('rel', 'noopener noreferrer')
  })
}

function wrapImagesForLightbox(root) {
  root.querySelectorAll('img[src]').forEach((img) => {
    if (img.parentNode?.tagName === 'A') return
    const a = document.createElement('a')
    a.href = img.src
    a.dataset.photoswipe = 'email-body-images'
    a.dataset.type = 'image'
    a.dataset.pswpWidth = String(img.naturalWidth || img.width || 800)
    a.dataset.pswpHeight = String(img.naturalHeight || img.height || 600)
    a.style.cursor = 'pointer'
    img.parentNode?.insertBefore(a, img)
    a.appendChild(img)
  })
}

export function EmailHtmlFrame({ html, linkClicks }) {
  const hostRef = useRef(null)
  const linkClicksKey = JSON.stringify(linkClicks ?? null)

  useEffect(() => {
    const host = hostRef.current
    if (!host) return undefined

    const shadow = host.shadowRoot || host.attachShadow({ mode: 'open' })
    let prepared = prepareEmailBodyHtml(html)
    if (linkClicks !== undefined) {
      prepared = annotateEmailBodyLinkClicks(prepared, linkClicks ?? [])
    }
    prepared = adaptEmailHtmlForIsolatedRoot(prepared, `.${ROOT_CLASS}`)
    const baseCss = emailBodyBaseCss({
      ...EMAIL_BODY_CANVAS_DEFAULTS,
      rootSelector: `.${ROOT_CLASS}`,
    })

    shadow.innerHTML = `<style>${baseCss}</style><div class="${ROOT_CLASS}">${prepared}</div>`

    const root = shadow.querySelector(`.${ROOT_CLASS}`)
    if (!root) return undefined

    hardenLinks(shadow)
    wrapImagesForLightbox(shadow)

    let lightbox
    if (shadow.querySelector('a[data-photoswipe]')) {
      lightbox = createPhotoSwipeLightbox(root)
    }

    return () => {
      lightbox?.destroy?.()
      shadow.innerHTML = ''
    }
  }, [html, linkClicksKey])

  return <div ref={hostRef} class="email-html-body-frame" />
}
